import { Component, OnInit, OnDestroy } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ChatbotService } from "../chatbot.service";
@Component({
  selector: "app-chatbot-widget",
  templateUrl: "./chatbot-widget.component.html",
  styleUrls: ["./chatbot-widget.component.css"],
})
export class ChatbotWidgetComponent implements OnInit, OnDestroy {
  isOpen = false;
  isLoaded = false;
  isLoadingToken = false;
  hasError = false;

  chatbotSrc: SafeResourceUrl | null = null;
  private rawChatbotUrl: string | null = null;
  private destroy$ = new Subject<void>();

  // History state
  showHistory = false;
  historyItems: any[] = [];
  historyPage = 1;
  historySize = 10;
  historyTotal = 0;
  isLoadingHistory = false;
  historyAllLoaded = false;
  currentConversationId: string | null = null;
  private pendingHistoryRefresh = false;

  constructor(
    private sanitizer: DomSanitizer,
    private chatbotService: ChatbotService,
  ) { }

  ngOnInit() { }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleChat() {
    this.isOpen = !this.isOpen;
    if (this.isOpen && !this.isLoaded && !this.isLoadingToken) {
      this.loadChatbot();
    }
  }

  closeChat() {
    this.isOpen = false;
  }

  restartChat() {
    // If history is open, close it first
    this.showHistory = false;
    this.currentConversationId = null;

    this.chatbotSrc = null;
    this.isLoaded = false;
    this.hasError = false;

    setTimeout(() => {
      if (this.rawChatbotUrl) {
        this.chatbotSrc = this.sanitizer.bypassSecurityTrustResourceUrl(this.rawChatbotUrl);
        this.isLoaded = true;
        this.refreshHistory();
      } else {
        this.loadChatbot();
      }
    }, 100);
  }

  retryLoad() {
    this.hasError = false;
    this.loadChatbot();
  }

  // ── History Methods ──────────────────────────────────────────────

  toggleHistory() {
    this.showHistory = !this.showHistory;
    if (this.showHistory) {
      this.resetHistory();
      this.loadHistory();
    }
  }

  closeHistory() {
    this.showHistory = false;
  }

  openInNewTab() {
    const conversationId = this.currentConversationId;
    this.endCurrentChat();
    this.chatbotService.openWorkspaceInNewTab(conversationId);
  }

  selectHistory(item: any) {
    if (!item || !item.id || !this.rawChatbotUrl) return;

    this.currentConversationId = item.id;

    // Reset loading state to show placeholder and hide old content
    this.isLoaded = false;
    this.chatbotSrc = null;

    const newChatUrl = this.chatbotService.appendConversationId(this.rawChatbotUrl, item.id);

    // Update the iframe source with a small delay to ensure DOM refresh
    setTimeout(() => {
      this.chatbotSrc = this.sanitizer.bypassSecurityTrustResourceUrl(newChatUrl);
      this.isLoaded = true;
    }, 50);

    // Close history view to show the chat
    this.showHistory = false;
    this.refreshHistory();
  }

  onHistoryScroll(event: any) {
    const el = event.target;
    const threshold = 80; // px from bottom
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= threshold) {
      if (!this.isLoadingHistory && !this.historyAllLoaded) {
        this.loadHistory();
      }
    }
  }

  private resetHistory() {
    this.historyItems = [];
    this.historyPage = 1;
    this.historyTotal = 0;
    this.historyAllLoaded = false;
  }

  private refreshHistory() {
    if (this.isLoadingHistory) {
      this.pendingHistoryRefresh = true;
      return;
    }

    this.pendingHistoryRefresh = false;
    this.resetHistory();
    this.loadHistory();
  }

  private loadHistory() {
    if (this.isLoadingHistory || this.historyAllLoaded) return;

    this.isLoadingHistory = true;
    this.chatbotService
      .getHistory(this.historyPage, this.historySize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res && res.items) {
            this.historyItems = [...this.historyItems, ...res.items];
            this.historyTotal =
              typeof res.total === "number"
                ? Math.max(res.total, this.historyItems.length)
                : this.historyItems.length;
            // Check if all pages loaded
            if (this.historyPage >= res.pages || res.items.length === 0) {
              this.historyAllLoaded = true;
            } else {
              this.historyPage++;
            }
          } else {
            this.historyTotal = this.historyItems.length;
            this.historyAllLoaded = true;
          }
          this.isLoadingHistory = false;
          if (this.pendingHistoryRefresh) {
            this.refreshHistory();
          }
        },
        error: (err) => {
          console.error("Failed to fetch chatbot history:", err);
          this.isLoadingHistory = false;
          if (this.pendingHistoryRefresh) {
            this.refreshHistory();
          }
        },
      });
  }

  // ── Private ──────────────────────────────────────────────

  private loadChatbot() {
    this.hasError = false;
    this.isLoadingToken = true;
    this.chatbotService.getChatbotUrl().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.rawChatbotUrl = res;
        this.chatbotSrc = this.sanitizer.bypassSecurityTrustResourceUrl(res);
        this.isLoaded = true;
        this.isLoadingToken = false;
        this.refreshHistory();
      },
      error: (err) => {
        console.error("Failed to fetch token for chatbot:", err);
        this.hasError = true;
        this.isLoadingToken = false;
      },
    });
  }

  private endCurrentChat() {
    this.closeHistory();
    this.closeChat();
    this.currentConversationId = null;
    this.chatbotSrc = null;
    this.isLoaded = false;
    this.hasError = false;
  }
}
